import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ArrowRight, Plus, Trash2, Save, ChevronDown, ChevronUp,
  Box, Cog, Package, TrendingUp, Star, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const STAGES = [
  { key: 'inputs', label: 'Inputs', icon: Box, color: '#4338CA', bgColor: 'bg-indigo-50 border-indigo-200', description: 'Resources invested' },
  { key: 'activities', label: 'Activities', icon: Cog, color: '#1D4ED8', bgColor: 'bg-blue-50 border-blue-200', description: 'Actions taken' },
  { key: 'outputs', label: 'Outputs', icon: Package, color: '#6D28D9', bgColor: 'bg-violet-50 border-violet-200', description: 'Direct deliverables' },
  { key: 'outcomes', label: 'Outcomes', icon: TrendingUp, color: '#B45309', bgColor: 'bg-amber-50 border-amber-200', description: 'Changes observed' },
  { key: 'impacts', label: 'Impacts', icon: Star, color: '#047857', bgColor: 'bg-emerald-50 border-emerald-200', description: 'Long-term effects' },
];

const TheoryOfChange = ({ evalId, readOnly = false }) => {
  const [toc, setToc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchToC();
  }, [evalId]);

  const fetchToC = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/investment-evaluation/evaluations/${evalId}/toc`, { headers });
      setToc(res.data);
    } catch {
      setToc(null);
    } finally {
      setLoading(false);
    }
  };

  const openEditor = () => {
    setEditData(toc ? JSON.parse(JSON.stringify(toc)) : {
      inputs: [], activities: [], outputs: [], outcomes: [], impacts: [],
      assumptions: [], risks: [],
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/api/investment-evaluation/evaluations/${evalId}/toc`, editData, { headers });
      toast.success('Theory of Change saved');
      setEditing(false);
      fetchToC();
    } catch {
      toast.error('Failed to save');
    }
  };

  const addItem = (stageKey) => {
    setEditData(prev => ({
      ...prev,
      [stageKey]: [...(prev[stageKey] || []), { id: '', title: '', description: '', value: '', indicator: '', target: '', timeframe: '', sdg_alignment: [], linked_ids: [] }],
    }));
  };

  const updateItem = (stageKey, idx, field, value) => {
    setEditData(prev => {
      const items = [...prev[stageKey]];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, [stageKey]: items };
    });
  };

  const removeItem = (stageKey, idx) => {
    setEditData(prev => ({
      ...prev,
      [stageKey]: prev[stageKey].filter((_, i) => i !== idx),
    }));
  };

  const addAssumptionOrRisk = (key) => {
    setEditData(prev => ({ ...prev, [key]: [...(prev[key] || []), ''] }));
  };

  const updateAssumptionOrRisk = (key, idx, value) => {
    setEditData(prev => {
      const items = [...prev[key]];
      items[idx] = value;
      return { ...prev, [key]: items };
    });
  };

  const removeAssumptionOrRisk = (key, idx) => {
    setEditData(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div></div>;
  }

  const hasData = toc && STAGES.some(s => (toc[s.key] || []).length > 0);

  // ========== VIEW MODE ==========
  return (
    <div className="space-y-4" data-testid="toc-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Theory of Change</h3>
          <p className="text-xs text-gray-500 mt-0.5">Inputs → Activities → Outputs → Outcomes → Impacts</p>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={openEditor} data-testid="edit-toc-btn">
            {hasData ? 'Edit' : 'Create'} ToC
          </Button>
        )}
      </div>

      {!hasData ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Box className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 text-center">No Theory of Change defined yet.</p>
            {!readOnly && <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700" onClick={openEditor}>Create Theory of Change</Button>}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Visual flow */}
          <div className="flex flex-col gap-0">
            {STAGES.map((stage, stageIdx) => {
              const raw = toc[stage.key];
              const items = Array.isArray(raw)
                ? raw.map((it) => (typeof it === 'string' ? { title: it } : it))
                : (raw && typeof raw === 'string' ? [{ title: raw }] : []);
              if (items.length === 0) return null;
              const Icon = stage.icon;
              const isExpanded = expandedStage === stage.key;
              return (
                <div key={stage.key}>
                  {stageIdx > 0 && items.length > 0 && (
                    <div className="flex justify-center py-1">
                      <ArrowRight className="h-5 w-5 text-gray-300 rotate-90" />
                    </div>
                  )}
                  <Card className={`border ${stage.bgColor}`} data-testid={`toc-stage-${stage.key}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedStage(isExpanded ? null : stage.key)}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" style={{ color: stage.color }} />
                          <span className="text-sm font-semibold" style={{ color: stage.color }}>{stage.label}</span>
                          <Badge variant="outline" className="text-xs">{items.length}</Badge>
                        </div>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                      </div>
                      {!isExpanded && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {items.map((item, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-normal bg-white/80">{item.title}</Badge>
                          ))}
                        </div>
                      )}
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {items.map((item, i) => (
                            <div key={i} className="bg-white rounded-md p-2.5 border border-gray-100">
                              <p className="text-sm font-medium text-gray-900">{item.title}</p>
                              {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                                {item.value && <span className="text-xs text-gray-500">Value: <span className="text-gray-600">{item.value}</span></span>}
                                {item.indicator && <span className="text-xs text-gray-500">Indicator: <span className="text-gray-600">{item.indicator}</span></span>}
                                {item.target && <span className="text-xs text-gray-500">Target: <span className="text-gray-600">{item.target}</span></span>}
                                {item.timeframe && <span className="text-xs text-gray-500">Timeframe: <span className="text-gray-600">{item.timeframe}</span></span>}
                                {Array.isArray(item.sdg_alignment) && item.sdg_alignment.length > 0 && (
                                  <span className="text-xs text-gray-500">SDGs: {item.sdg_alignment.map(s => <Badge key={s} className="bg-green-100 text-green-700 text-xs ml-0.5">{s}</Badge>)}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>

          {/* Assumptions & Risks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array.isArray(toc.assumptions) && toc.assumptions.length > 0 && (
              <Card className="bg-blue-50/50 border-blue-100">
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <ShieldCheck className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-semibold text-blue-700">Assumptions</span>
                  </div>
                  <ul className="space-y-1">
                    {toc.assumptions.map((a, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <span className="text-blue-400 mt-0.5">-</span>{a}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {Array.isArray(toc.risks) && toc.risks.length > 0 && (
              <Card className="bg-red-50/50 border-red-100">
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-xs font-semibold text-red-700">Risks</span>
                  </div>
                  <ul className="space-y-1">
                    {toc.risks.map((r, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <span className="text-red-400 mt-0.5">-</span>{r}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Editor Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Theory of Change</DialogTitle></DialogHeader>
          {editData && (
            <div className="space-y-5 pt-2">
              {STAGES.map(stage => {
                const Icon = stage.icon;
                const rawEdit = editData[stage.key];
                const items = Array.isArray(rawEdit)
                  ? rawEdit.map((it) => (typeof it === 'string' ? { title: it } : it))
                  : (rawEdit && typeof rawEdit === 'string' ? [{ title: rawEdit }] : []);
                return (
                  <div key={stage.key}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: stage.color }} />
                        <span className="text-sm font-semibold" style={{ color: stage.color }}>{stage.label}</span>
                        <span className="text-xs text-gray-500">{stage.description}</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => addItem(stage.key)} data-testid={`add-${stage.key}`}>
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    </div>
                    {items.length === 0 && <p className="text-xs text-gray-500 ml-6 italic">No items yet</p>}
                    <div className="space-y-2 ml-6">
                      {items.map((item, idx) => (
                        <div key={idx} className="border rounded-lg p-3 bg-gray-50/50">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Title</Label>
                                <Input value={item.title} onChange={e => updateItem(stage.key, idx, 'title', e.target.value)} className="h-8 text-sm" placeholder="Title..." />
                              </div>
                              {(stage.key === 'inputs') && (
                                <div>
                                  <Label className="text-xs">Value / Resource</Label>
                                  <Input value={item.value || ''} onChange={e => updateItem(stage.key, idx, 'value', e.target.value)} className="h-8 text-sm" placeholder="EUR 50,000" />
                                </div>
                              )}
                              {(stage.key === 'outputs' || stage.key === 'outcomes') && (
                                <>
                                  <div>
                                    <Label className="text-xs">Indicator</Label>
                                    <Input value={item.indicator || ''} onChange={e => updateItem(stage.key, idx, 'indicator', e.target.value)} className="h-8 text-sm" placeholder="Measurable indicator" />
                                  </div>
                                </>
                              )}
                              {(stage.key === 'outcomes') && (
                                <div>
                                  <Label className="text-xs">Timeframe</Label>
                                  <Input value={item.timeframe || ''} onChange={e => updateItem(stage.key, idx, 'timeframe', e.target.value)} className="h-8 text-sm" placeholder="6 months" />
                                </div>
                              )}
                              {(stage.key === 'outputs') && (
                                <div>
                                  <Label className="text-xs">Target</Label>
                                  <Input value={item.target || ''} onChange={e => updateItem(stage.key, idx, 'target', e.target.value)} className="h-8 text-sm" placeholder="200" />
                                </div>
                              )}
                            </div>
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 mt-4" onClick={() => removeItem(stage.key, idx)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="mt-2">
                            <Textarea value={item.description || ''} onChange={e => updateItem(stage.key, idx, 'description', e.target.value)} rows={1} className="text-xs" placeholder="Description..." />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Assumptions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-semibold text-blue-700">Assumptions</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => addAssumptionOrRisk('assumptions')}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                </div>
                {(Array.isArray(editData.assumptions) ? editData.assumptions : []).map((a, i) => (
                  <div key={i} className="flex gap-2 mb-1 ml-6">
                    <Input value={a} onChange={e => updateAssumptionOrRisk('assumptions', i, e.target.value)} className="h-8 text-xs" placeholder="Assumption..." />
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeAssumptionOrRisk('assumptions', i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>

              {/* Risks */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-semibold text-red-700">Risks</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => addAssumptionOrRisk('risks')}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                </div>
                {(Array.isArray(editData.risks) ? editData.risks : []).map((r, i) => (
                  <div key={i} className="flex gap-2 mb-1 ml-6">
                    <Input value={r} onChange={e => updateAssumptionOrRisk('risks', i, e.target.value)} className="h-8 text-xs" placeholder="Risk..." />
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeAssumptionOrRisk('risks', i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-toc-btn">
                  <Save className="h-4 w-4 mr-1" /> Save Theory of Change
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TheoryOfChange;
